import SeatLimitDialog, { seatLimitDetail } from "@/components/SeatLimitDialog";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageContainer, PageHeader, EmptyState } from "./_shared";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Edit3, Plus, Trash2, ShieldCheck, Mail, X, Send, Copy, Check, UserX, UserCheck, IdCard, Shirt, FileText, Sparkles, Truck, Search, LayoutList, LayoutGrid, DollarSign, MoreHorizontal, ChevronDown, AlertTriangle } from "lucide-react";
import { TeamMemberHr } from "@/components/HrCards";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";

const ROLE_LABEL = {
  super_admin: "Platform",
  admin: "Admin",
  office: "Office",
  field: "Field",
  mechanic: "Mechanic",
  quantity_surveyor: "Quantity Surveyor",
};

const ROLE_OPTIONS = ["all", "admin", "office", "field", "mechanic", "quantity_surveyor", "super_admin"];

const competencySummary = (userId, matrix, today) => {
  const cells = Object.values(matrix?.cells?.[userId] || {});
  const cutoff = new Date(`${today}T00:00:00Z`);
  cutoff.setUTCDate(cutoff.getUTCDate() + 30);
  const expiringCutoff = cutoff.toISOString().slice(0, 10);
  let current = 0;
  let expiring = 0;
  let expired = 0;
  for (const item of cells) {
    if (!item.expiry_date || item.expiry_date > expiringCutoff) {
      current += 1;
    } else if (item.expiry_date >= today) {
      expiring += 1;
    } else {
      expired += 1;
    }
  }
  return { total: cells.length, current, expiring, expired, attention: expiring + expired };
};

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const canInvite = isAdmin || user.role === "office";
  const canSuspend = isAdmin || user.role === "office";
  const canEditOwnRate = ["admin", "office", "super_admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [seatLimit, setSeatLimit] = useState(null);
  const [invites, setInvites] = useState([]);
  const [editing, setEditing] = useState(null);
  const [myRateTarget, setMyRateTarget] = useState(null);
  const [hrMember, setHrMember] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("fl_team_view") || "list");
  const setViewPersist = (v) => { setView(v); localStorage.setItem("fl_team_view", v); };
  const filterKey = `fl_team_filters_${user.org_id}_${user.id}`;
  const savedFilters = () => { try { return JSON.parse(sessionStorage.getItem(filterKey) || "{}"); } catch { return {}; } };
  const [q, setQ] = useState(() => savedFilters().q || "");
  const [roleFilter, setRoleFilter] = useState(() => savedFilters().roleFilter || "all");
  const [statusFilter, setStatusFilter] = useState(() => savedFilters().statusFilter || "all");
  const [competencyFilter, setCompetencyFilter] = useState(() => savedFilters().competencyFilter || "all");
  useEffect(() => { sessionStorage.setItem(filterKey, JSON.stringify({q, roleFilter, statusFilter, competencyFilter})); }, [filterKey, q, roleFilter, statusFilter, competencyFilter]);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [competencyMatrix, setCompetencyMatrix] = useState(null);
  const [today, setToday] = useState(new Date().toISOString().slice(0, 10));
  const [todayAssignments, setTodayAssignments] = useState([]);
  const [todayLeave, setTodayLeave] = useState([]);

  const copyInviteLink = async (inv) => {
    const url = inv.accept_url;
    if (!url) { toast.error("No invite link available"); return; }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Invite link copied — paste it into WhatsApp, SMS or Slack");
      // Defer the state-driven button swap one frame so toast portal mounts cleanly
      // (avoids a benign React 19 + sonner DOM-commit warning in dev mode).
      requestAnimationFrame(() => {
        setCopiedId(inv.id);
        setTimeout(() => setCopiedId((id) => (id === inv.id ? null : id)), 2500);
      });
    } catch (e) {
      toast.error("Could not copy. Long-press the link to copy manually.");
    }
  };

  const load = async () => {
    const currentDate = new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });
    const [{ data }] = await Promise.all([api.get("/users")]);
    setItems(data);
    try { const r = await api.get("/billing/entitlement"); setCapacity(r.data); } catch { setCapacity(null); }
    setToday(currentDate);
    try { const r = await api.get("/assignments", { params: { date: currentDate } }); setTodayAssignments(r.data || []); } catch (e) { setTodayAssignments([]); }
    try { const r = await api.get("/leave"); setTodayLeave(r.data || []); } catch (e) { setTodayLeave([]); }
    if (canInvite) {
      try { const r = await api.get("/invites"); setInvites(r.data); } catch (e) { /* ignore — non-fatal */ }
      try { const r = await api.get("/competencies/matrix"); setCompetencyMatrix(r.data); } catch (e) { setCompetencyMatrix(null); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (uid) => {
    if (!window.confirm("Remove this team member from your company?")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const suspend = async (u) => {
    if (!window.confirm(`Suspend ${u.name}? They'll be logged out and locked out immediately, and removed from today's + future dispatch. Their timesheets and history stay intact. You can reactivate them any time.`)) return;
    try {
      await api.post(`/users/${u.id}/suspend`);
      toast.success(`${u.name} suspended — access cut off`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suspend failed");
    }
  };

  const unsuspend = async (u) => {
    try {
      await api.post(`/users/${u.id}/unsuspend`);
      toast.success(`${u.name} reactivated`);
      load();
    } catch (e) {
      if (seatLimitDetail(e)) { setSeatLimit(seatLimitDetail(e)); load(); }
      else toast.error(e.response?.data?.detail || "Reactivate failed");
    }
  };

  const toggleAdvancedField = async (u) => {
    const next = !u.advanced_field;
    try {
      await api.patch(`/users/${u.id}/advanced-field`, { advanced_field: next });
      toast.success(next
        ? `${u.name} is now an Advanced fieldworker — they'll see activity cost codes on their timesheets`
        : `${u.name} switched back to a normal field worker`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleLandfillOperator = async (u) => {
    try {
      await api.patch(`/users/${u.id}/landfill-operator`, { landfill_operator: !u.landfill_operator });
      toast.success(u.landfill_operator ? 'Landfill access removed' : 'Landfill access added — Fieldworker access retained');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleTruckDriver = async (u) => {
    const next = !u.truck_driver;
    try {
      await api.patch(`/users/${u.id}/truck-driver`, { truck_driver: next });
      toast.success(next
        ? `${u.name} is now a Truck driver — they'll see the Running Sheets page`
        : `${u.name} is no longer a truck driver`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const [contractorDialog, setContractorDialog] = useState(null); // user being configured

  const toggleContractor = async (u) => {
    if (u.contractor) { setContractorDialog(u); return; }
    try {
      await api.patch(`/users/${u.id}/contractor`, { contractor: true });
      toast.success(`${u.name} is now a Contractor — they can invoice you from approved timesheets`);
      load();
      setContractorDialog({ ...u, contractor: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const changeRole = async (uid, newRole, currentRole) => {
    if (newRole === currentRole) return;
    const label = ROLE_LABEL[newRole] || newRole;
    if (!window.confirm(`Change this teammate's role to ${label}? They'll see the new permissions next time they reload.`)) {
      load(); // revert the dropdown
      return;
    }
    try {
      await api.patch(`/users/${uid}/role`, { role: newRole });
      toast.success(`Role changed to ${label}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Role change failed");
      load();
    }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite? The link will stop working.")) return;
    try {
      await api.delete(`/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const pendingInvites = invites.filter((i) => !i.accepted_at && !i.revoked_at);
  const competencyFor = (u) => competencySummary(u.id, competencyMatrix, today);
  const leaveFor = (u) => todayLeave.some((leave) => leave.user_id === u.id && leave.status === "approved" && leave.start_date <= today && leave.end_date >= today);
  const assignmentFor = (u) => todayAssignments.filter((assignment) => assignment.user_id === u.id);
  const todayFor = (u) => {
    if (leaveFor(u)) return { label: "ON LEAVE", type: "leave" };
    const assignments = assignmentFor(u).filter((assignment) => assignment.job_id);
    if (!assignments.length) return { label: "NOT ASSIGNED", type: "none" };
    return { label: assignments.map((assignment) => [assignment.job_id, assignment.job_title].filter(Boolean).join(" · ")).join(", "), type: "job" };
  };
  const qLower = q.trim().toLowerCase();
  const filtered = useMemo(() => items.filter((u) => {
    const summary = competencyFor(u);
    const matchesQuery = !qLower || [u.name, u.email, ROLE_LABEL[u.role] || u.role].filter(Boolean).join(" ").toLowerCase().includes(qLower);
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    const matchesStatus = statusFilter === "all" || (statusFilter === "inactive" ? !!u.suspended_at : !u.suspended_at);
    const matchesCompetency = competencyFilter === "all"
      || (competencyFilter === "attention" && summary.attention > 0)
      || (competencyFilter === "current" && summary.total > 0 && summary.attention === 0)
      || (competencyFilter === "none" && summary.total === 0);
    return matchesQuery && matchesRole && matchesStatus && matchesCompetency;
  }), [items, qLower, roleFilter, statusFilter, competencyFilter, competencyMatrix, today]);

  const workforceMetrics = useMemo(() => {
    const summaries = items.map(competencyFor);
    return {
      total: items.length,
      field: items.filter((u) => u.role === "field" || u.role === "mechanic").length,
      office: items.filter((u) => !["field", "mechanic"].includes(u.role)).length,
      issues: summaries.filter((summary) => summary.attention > 0).length,
      onLeave: items.filter(leaveFor).length,
      pendingInvites: pendingInvites.length,
    };
  }, [items, competencyMatrix, today, todayLeave, pendingInvites.length]);

  return <WorkforceDirectory
    capacity={capacity} seatLimit={seatLimit} setSeatLimit={setSeatLimit}
    user={user}
    isAdmin={isAdmin}
    canInvite={canInvite}
    canSuspend={canSuspend}
    canEditOwnRate={canEditOwnRate}
    items={items}
    filtered={filtered}
    pendingInvites={pendingInvites}
    workforceMetrics={workforceMetrics}
    competencyFor={competencyFor}
    todayFor={todayFor}
    q={q}
    setQ={setQ}
    roleFilter={roleFilter}
    setRoleFilter={setRoleFilter}
    statusFilter={statusFilter}
    setStatusFilter={setStatusFilter}
    competencyFilter={competencyFilter}
    setCompetencyFilter={setCompetencyFilter}
    view={view}
    setViewPersist={setViewPersist}
    invitesOpen={invitesOpen}
    setInvitesOpen={setInvitesOpen}
    copiedId={copiedId}
    copyInviteLink={copyInviteLink}
    revokeInvite={revokeInvite}
    load={load}
    inviteOpen={inviteOpen}
    setInviteOpen={setInviteOpen}
    addOpen={addOpen}
    setAddOpen={setAddOpen}
    editing={editing}
    setEditing={setEditing}
    myRateTarget={myRateTarget}
    setMyRateTarget={setMyRateTarget}
    hrMember={hrMember}
    setHrMember={setHrMember}
    contractorDialog={contractorDialog}
    setContractorDialog={setContractorDialog}
    changeRole={changeRole}
    suspend={suspend}
    unsuspend={unsuspend}
    remove={remove}
    toggleAdvancedField={toggleAdvancedField}
    toggleTruckDriver={toggleTruckDriver}
    toggleLandfillOperator={toggleLandfillOperator}
    toggleContractor={toggleContractor}
    openWorker={(member) => navigate(`/app/team/${encodeURIComponent(member.id)}`)}
  />;

  return (
    <PageContainer variant="wide" data-testid="team-page">
      <PageHeader
        eyebrow="Workforce // People"
        title="Team"
        subtitle="Manage your people, roles, access, competencies and workforce availability."
        action={canInvite && (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="add-person-button">
                  <Plus className="w-4 h-4 mr-1.5" /> Add person <ChevronDown className="w-3.5 h-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onSelect={() => setInviteOpen(true)} data-testid="invite-member-button"><Mail className="mr-2 h-4 w-4" /> Send invite</DropdownMenuItem>
                {isAdmin && <DropdownMenuItem onSelect={() => setAddOpen(true)} data-testid="add-member-button"><Plus className="mr-2 h-4 w-4" /> Add directly</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      />

      <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-zinc-200 bg-zinc-200 dark:border-white/10 dark:bg-white/10 sm:grid-cols-3 min-[1200px]:grid-cols-6" data-testid="workforce-summary">
        {[["TOTAL PEOPLE", workforceMetrics.total], ["FIELD", workforceMetrics.field], ["OFFICE", workforceMetrics.office], ["COMPETENCY ISSUES", workforceMetrics.issues]].map(([label, value]) => (
          <div key={label} className="bg-white px-3 py-3 dark:bg-[#0F141C]">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</div>
            <div className="mt-1 text-2xl font-black tracking-tight text-zinc-900 dark:text-white">{value}</div>
          </div>
        ))}
      </div>

      {pendingInvites.length > 0 && (
        <div className="mb-4 rounded-sm border border-zinc-200 bg-white dark:border-white/10 dark:bg-[#0F141C]" data-testid="pending-invites-section">
          <button onClick={() => setInvitesOpen((open) => !open)} className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left" data-testid="pending-invites-toggle">
            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 dark:text-zinc-300"><Send className="h-3 w-3 text-[#FF5F15]" /> Pending invites <strong className="text-[#FF5F15]">{pendingInvites.length}</strong></span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{invitesOpen ? "Hide" : "View"} <ChevronDown className={`ml-1 inline h-3.5 w-3.5 transition-transform ${invitesOpen ? "rotate-180" : ""}`} /></span>
          </button>
          {invitesOpen && <div className="border-t border-zinc-200 px-3 dark:border-white/10">
            <div className="hidden grid-cols-[2fr_1fr_1fr_auto] gap-3 py-2 text-[9px] font-bold uppercase tracking-wider text-zinc-500 md:grid"><span>Person / Email</span><span>Role</span><span>Sent / Expires</span><span>Actions</span></div>
            {pendingInvites.map((inv) => (
              <div key={inv.id} className="grid gap-2 border-t border-zinc-200 py-3 first:border-t-0 md:grid-cols-[2fr_1fr_1fr_auto] md:items-center dark:border-white/10" data-testid={`invite-row-${inv.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold truncate">{inv.name} <span className="text-zinc-400 font-normal">· {ROLE_LABEL[inv.role] || inv.role}</span></div>
                  <div className="text-xs text-zinc-500 truncate font-mono">{inv.email}</div>
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{ROLE_LABEL[inv.role] || inv.role}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Expires {new Date(inv.expires_at).toLocaleDateString("en-NZ", { month: "short", day: "numeric" })}</div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className={`h-8 rounded-sm text-[10px] font-bold uppercase tracking-wider ${
                    copiedId === inv.id
                      ? "border-green-600 text-green-700 bg-green-50 hover:bg-green-50"
                      : "border-[#FF5F15] text-[#FF5F15] hover:bg-[#FFF7ED]"
                  }`}
                  onClick={() => copyInviteLink(inv)}
                  disabled={!inv.accept_url}
                  data-testid={`invite-copy-${inv.id}`}
                  >
                  {copiedId === inv.id ? (
                    <><Check className="w-3.5 h-3.5 mr-1" /> Copied</>
                  ) : (
                    <><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</>
                  )}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 rounded-sm text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-red-600" onClick={() => revokeInvite(inv.id)} data-testid={`invite-revoke-${inv.id}`}>Revoke</Button>
                </div>
              </div>
            ))}
          </div>}
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 rounded-sm border border-zinc-200 bg-white p-3 dark:border-white/10 dark:bg-[#0F141C] xl:flex-row xl:items-center" data-testid="team-toolbar">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people..."
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620] text-sm outline-none focus:border-[#FF5F15] transition-colors"
            data-testid="team-search-input"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 xl:ml-auto">
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-[10px] font-bold uppercase tracking-wider dark:border-white/10 dark:bg-[#101620]" data-testid="team-role-filter">
            {ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role === "all" ? "ROLE" : ROLE_LABEL[role] || role}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-[10px] font-bold uppercase tracking-wider dark:border-white/10 dark:bg-[#101620]" data-testid="team-status-filter">
            <option value="all">STATUS</option><option value="active">ACTIVE</option><option value="inactive">INACTIVE</option>
          </select>
          <select value={competencyFilter} onChange={(e) => setCompetencyFilter(e.target.value)} className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-[10px] font-bold uppercase tracking-wider dark:border-white/10 dark:bg-[#101620]" data-testid="team-competency-filter">
            <option value="all">COMPETENCY</option><option value="attention">NEEDS ATTENTION</option><option value="current">CURRENT</option><option value="none">NO RECORDS</option>
          </select>
        </div>
        <div className="flex items-center rounded-sm border border-zinc-300 bg-white p-0.5 dark:border-white/15 dark:bg-[#101620]" data-testid="team-view-toggle">
          <button
            onClick={() => setViewPersist("list")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${view === "list" ? "bg-[#111827] text-white dark:bg-[#FF5F15]" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}
            data-testid="team-view-list">
            <LayoutList className="w-3.5 h-3.5" /> List
          </button>
          <button
            onClick={() => setViewPersist("cards")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${view === "cards" ? "bg-[#111827] text-white dark:bg-[#FF5F15]" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}
            data-testid="team-view-cards">
            <LayoutGrid className="w-3.5 h-3.5" /> Cards
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No team members yet" description="Add your first crew member to get started." />
      ) : filtered.length === 0 ? (
        <EmptyState title="No matches" description="Try a different search." />
      ) : view === "list" && window.innerWidth >= 768 ? (
        <div className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] overflow-hidden" data-testid="team-list">
          {filtered.map((u) => {
            const b = u.leave_balances || {};
            const isSelf = u.id === user.id;
            const isSuspended = !!u.suspended_at;
            const lic = u.licence || {};
            const licDays = lic.expiry ? Math.floor((new Date(lic.expiry) - Date.now()) / 86400000) : null;
            return (
              <div key={u.id}
                   className={`flex items-center gap-3 px-4 py-3 border-b border-zinc-100 dark:border-white/5 last:border-b-0 hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition-colors ${isSuspended ? "bg-zinc-50/60 dark:bg-white/[0.02]" : ""}`}
                   data-testid={`team-row-${u.id}`}>
                {u.avatar_path ? (
                  <div className={`w-10 h-10 rounded-lg shrink-0 overflow-hidden border border-zinc-300 dark:border-white/10 ${isSuspended ? "opacity-50" : ""}`}>
                    <AuthImage path={u.avatar_path} alt={u.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <Avatar className={`w-10 h-10 rounded-lg shrink-0 ${isSuspended ? "opacity-50" : ""}`}>
                    <AvatarFallback className={`rounded-lg font-display font-black text-sm text-white ${u.role === "super_admin" ? "bg-zinc-900" : u.role === "admin" ? "bg-[#FF5F15]" : u.role === "office" ? "bg-zinc-800" : "bg-zinc-600"}`}>
                      {u.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className={`min-w-0 flex-1 ${isSuspended ? "opacity-60" : ""}`}>
                  <div className="font-display font-bold uppercase tracking-tight text-[15px] truncate flex items-center gap-1.5">
                    {u.name}
                    {u.role === "super_admin" && <ShieldCheck className="w-3.5 h-3.5 text-[#FF5F15]" />}
                    {isSelf && <span className="text-[9px] font-mono font-normal text-zinc-400 normal-case tracking-normal">· you</span>}
                    {isSuspended && (
                      <span className="bg-red-100 text-red-700 border border-red-300 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" data-testid={`suspended-badge-${u.id}`}>Suspended</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                </div>

                {/* Role */}
                <div className="w-[150px] shrink-0 hidden sm:block">
                  {isAdmin && !isSelf && u.role !== "super_admin" && !isSuspended ? (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v, u.role)}>
                      <SelectTrigger className="h-8 w-full rounded-lg border-zinc-300 dark:border-white/15 text-[10px] uppercase tracking-widest font-bold" data-testid={`role-select-${u.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="office">Office</SelectItem>
                        <SelectItem value="field">Field worker</SelectItem>
                        <SelectItem value="mechanic">Mechanic</SelectItem>
                        <SelectItem value="quantity_surveyor">Quantity Surveyor</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="label-tag">{ROLE_LABEL[u.role] || u.role}</span>
                  )}
                </div>

                {/* Field-worker toggles (icon chips) */}
                <div className="hidden lg:flex items-center gap-1 w-[140px] shrink-0">
                  {u.role === "field" && !isSuspended && canSuspend && (
                    <>
                      <button onClick={() => toggleAdvancedField(u)} data-testid={`advanced-field-toggle-${u.id}`}
                        title={u.advanced_field ? "Advanced fieldworker — click to switch off" : "Make Advanced fieldworker (activity cost codes)"}
                        className={`w-7 h-7 rounded-md border flex items-center justify-center transition-colors ${u.advanced_field ? "border-[#FF5F15] bg-orange-50 dark:bg-[#FF5F15]/15 text-[#FF5F15]" : "border-zinc-200 dark:border-white/10 text-zinc-300 dark:text-zinc-600 hover:border-zinc-400 hover:text-zinc-500"}`}>
                        <Sparkles className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => toggleLandfillOperator(u)} aria-pressed={!!u.landfill_operator}
                        aria-label="Landfill Operator" title="Landfill Operator — additional load recording access"
                        className={`w-7 h-7 rounded-md border text-xs ${u.landfill_operator ? 'border-[#FF5F15] bg-orange-50 text-[#FF5F15]' : 'border-zinc-200 text-zinc-400'}`}>LF</button>
                      <button onClick={() => toggleTruckDriver(u)} data-testid={`truck-driver-toggle-${u.id}`}
                        title={u.truck_driver ? "Truck driver — click to switch off" : "Make Truck driver (running sheets)"}
                        className={`w-7 h-7 rounded-md border flex items-center justify-center transition-colors ${u.truck_driver ? "border-[#FF5F15] bg-orange-50 dark:bg-[#FF5F15]/15 text-[#FF5F15]" : "border-zinc-200 dark:border-white/10 text-zinc-300 dark:text-zinc-600 hover:border-zinc-400 hover:text-zinc-500"}`}>
                        <Truck className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => toggleContractor(u)} data-testid={`contractor-toggle-${u.id}`}
                        title={u.contractor ? `Contractor${u.contractor_rate ? ` @ $${u.contractor_rate}/hr` : ""} — click to edit` : "Mark as Contractor (invoices from timesheets)"}
                        className={`w-7 h-7 rounded-md border flex items-center justify-center transition-colors ${u.contractor ? "border-[#FF5F15] bg-orange-50 dark:bg-[#FF5F15]/15 text-[#FF5F15]" : "border-zinc-200 dark:border-white/10 text-zinc-300 dark:text-zinc-600 hover:border-zinc-400 hover:text-zinc-500"}`}>
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>

                {/* Licence status */}
                {canSuspend && (
                  <button onClick={() => setHrMember(u)}
                    className={`hidden xl:inline-flex items-center gap-1 border px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide w-[130px] justify-center shrink-0 transition-colors ${
                      !lic.number && !lic.expiry ? "border-zinc-200 dark:border-white/10 text-zinc-400 hover:border-zinc-400"
                      : licDays != null && licDays < 0 ? "border-red-400 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400"
                      : licDays != null && licDays < 60 ? "border-amber-400 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400"
                      : "border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"}`}
                    data-testid={`chip-licence-${u.id}`}>
                    <IdCard className="w-3 h-3" />
                    {!lic.number && !lic.expiry ? "No licence"
                      : licDays != null && licDays < 0 ? "Expired"
                      : licDays != null && licDays < 60 ? `${licDays}d left`
                      : `Licence${lic.licence_class ? ` Cl ${lic.licence_class}` : ""}`}
                  </button>
                )}

                {/* Leave balances */}
                <div className="hidden md:flex items-center gap-2.5 w-[168px] shrink-0 text-[11px] font-mono tabular-nums" title="Leave balances — Annual · Sick · Bereavement">
                  <span className="text-zinc-500"><span className="text-zinc-400 text-[9px] uppercase">A</span> {b.annual ?? 0}</span>
                  <span className="text-zinc-500"><span className="text-zinc-400 text-[9px] uppercase">S</span> {b.sick ?? 0}</span>
                  <span className="text-zinc-500"><span className="text-zinc-400 text-[9px] uppercase">B</span> {b.bereavement ?? 0}</span>
                  {u.hourly_rate > 0 && <span className="ml-auto font-bold text-zinc-700 dark:text-zinc-300">${Number(u.hourly_rate).toFixed(0)}/hr</span>}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-0.5 shrink-0">
                  {canSuspend && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-md" onClick={() => setHrMember(u)} title="Licence, sizes & contract" data-testid={`hr-btn-${u.id}`}>
                      <IdCard className="w-3.5 h-3.5 text-[#FF5F15]" />
                    </Button>
                  )}
                  {isSelf && canEditOwnRate && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-md" onClick={() => setMyRateTarget(u)} title="Set my charge-out & pay rate" data-testid={`set-my-rate-${u.id}`}>
                      <DollarSign className="w-3.5 h-3.5 text-[#FF5F15]" />
                    </Button>
                  )}
                  {(isAdmin || canSuspend) && !isSelf && u.role !== "super_admin" && (
                    <>
                      {isAdmin && !isSuspended && !u.is_owner && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-md" onClick={() => setEditing(u)} title="Edit balances & rate" data-testid={`edit-balance-${u.id}`}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {canSuspend && (
                        isSuspended ? (
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-md text-green-700 hover:text-green-800 hover:bg-green-50" onClick={() => unsuspend(u)} title="Reactivate access" data-testid={`unsuspend-member-${u.id}`}>
                            <UserCheck className="w-3.5 h-3.5" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-md text-zinc-400 hover:text-amber-600" onClick={() => suspend(u)} title="Suspend access" data-testid={`suspend-member-${u.id}`}>
                            <UserX className="w-3.5 h-3.5" />
                          </Button>
                        )
                      )}
                      {isAdmin && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-md text-zinc-400 hover:text-red-600" onClick={() => remove(u.id)} title="Remove from company" data-testid={`remove-member-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filtered.map((u) => {
            const b = u.leave_balances || {};
            const isSelf = u.id === user.id;
            const isSuspended = !!u.suspended_at;
            const lic = u.licence || {};
            const licDays = lic.expiry ? Math.floor((new Date(lic.expiry) - Date.now()) / 86400000) : null;
            const hasSizes = Object.values(u.uniform_sizes || {}).some(Boolean);
            return (
              <div key={u.id}
                   className={`hard-card p-6 ${isSuspended ? "bg-zinc-50 border-zinc-300" : ""}`}
                   data-testid={`team-card-${u.id}`}>
                <div className={`flex items-start gap-4 ${isSuspended ? "opacity-60" : ""}`}>
                  {u.avatar_path ? (
                    <div className="w-16 h-16 rounded-sm shrink-0 overflow-hidden border border-zinc-300">
                      <AuthImage path={u.avatar_path} alt={u.name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                  <Avatar className="w-16 h-16 rounded-sm shrink-0">
                    <AvatarFallback className={`rounded-sm font-display font-black text-xl text-white ${u.role === "super_admin" ? "bg-zinc-900" : u.role === "admin" ? "bg-[#FF5F15]" : u.role === "office" ? "bg-zinc-800" : "bg-zinc-600"}`}>
                      {u.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-1.5">
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold uppercase tracking-tight text-lg truncate flex items-center gap-1.5">
                          {u.name}
                          {u.role === "super_admin" && <ShieldCheck className="w-4 h-4 text-[#FF5F15]" />}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                        {u.created_at && (
                          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-0.5">
                            Since {new Date(u.created_at).toLocaleDateString("en-NZ", { month: "short", year: "numeric" })}
                          </div>
                        )}
                      </div>
                      {canSuspend && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 rounded-sm shrink-0 -mt-1"
                          onClick={() => setHrMember(u)} title="Licence, sizes & contract"
                          data-testid={`hr-btn-${u.id}`}>
                          <IdCard className="w-3.5 h-3.5 text-[#FF5F15]" />
                        </Button>
                      )}
                      {isSelf && canEditOwnRate && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 rounded-sm shrink-0 -mt-1"
                          onClick={() => setMyRateTarget(u)} title="Set my charge-out & pay rate"
                          data-testid={`set-my-rate-${u.id}`}>
                          <DollarSign className="w-3.5 h-3.5 text-[#FF5F15]" />
                        </Button>
                      )}
                      {(isAdmin || canSuspend) && !isSelf && u.role !== "super_admin" && (
                        <div className="flex gap-0.5 shrink-0 -mt-1 -mr-1">
                          {isAdmin && !isSuspended && !u.is_owner && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 rounded-sm" onClick={() => setEditing(u)} data-testid={`edit-balance-${u.id}`}>
                              <Edit3 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canSuspend && (
                            isSuspended ? (
                              <Button size="icon" variant="ghost"
                                      className="h-7 w-7 rounded-sm text-green-700 hover:text-green-800 hover:bg-green-50"
                                      onClick={() => unsuspend(u)}
                                      title="Reactivate access"
                                      data-testid={`unsuspend-member-${u.id}`}>
                                <UserCheck className="w-3.5 h-3.5" />
                              </Button>
                            ) : (
                              <Button size="icon" variant="ghost"
                                      className="h-7 w-7 rounded-sm text-zinc-400 hover:text-amber-600"
                                      onClick={() => suspend(u)}
                                      title="Suspend access (keeps their history)"
                                      data-testid={`suspend-member-${u.id}`}>
                                <UserX className="w-3.5 h-3.5" />
                              </Button>
                            )
                          )}
                          {isAdmin && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 rounded-sm text-zinc-400 hover:text-red-600" onClick={() => remove(u.id)} data-testid={`remove-member-${u.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    {isSuspended && (
                      <span className="inline-block mt-1.5 bg-red-100 text-red-700 border border-red-300 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5"
                            data-testid={`suspended-badge-${u.id}`}>
                        Suspended {u.suspended_at ? new Date(u.suspended_at).toLocaleDateString("en-NZ") : ""}
                      </span>
                    )}
                    {/* Role: admins can change other teammates' roles; their own
                        role and super_admin badges are read-only here. */}
                    {isAdmin && !isSelf && u.role !== "super_admin" && !isSuspended ? (
                      <Select value={u.role} onValueChange={(v) => changeRole(u.id, v, u.role)}>
                        <SelectTrigger
                          className="mt-2 h-7 w-full max-w-[150px] rounded-sm border-zinc-300 text-[10px] uppercase tracking-widest font-bold"
                          data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="office">Office</SelectItem>
                          <SelectItem value="field">Field worker</SelectItem>
                          <SelectItem value="mechanic">Mechanic</SelectItem>
                          <SelectItem value="quantity_surveyor">Quantity Surveyor</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="label-tag mt-1">{ROLE_LABEL[u.role] || u.role}{isSelf && " · You"}</div>
                    )}
                    {u.role === "field" && !isSuspended && (
                      canSuspend ? (
                        <button
                          onClick={() => toggleAdvancedField(u)}
                          data-testid={`advanced-field-toggle-${u.id}`}
                          className={`mt-1.5 inline-flex items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                            u.advanced_field
                              ? "border-[#FF5F15] bg-orange-50 text-[#FF5F15]"
                              : "border-zinc-200 text-zinc-400 hover:border-zinc-400"
                          }`}
                          title={u.advanced_field
                            ? "Advanced fieldworker — sees activity cost codes on timesheets. Click to switch off."
                            : "Make Advanced fieldworker — adds an activity cost-code picker to their timesheets."}>
                          <Sparkles className="w-3 h-3" />
                          {u.advanced_field ? "Advanced fieldworker" : "Standard timesheets"}
                        </button>
                      ) : u.advanced_field ? (
                        <span className="mt-1.5 inline-flex items-center gap-1 border border-[#FF5F15] bg-orange-50 text-[#FF5F15] px-2 py-1 text-[10px] font-bold uppercase tracking-wide"
                              data-testid={`advanced-field-badge-${u.id}`}>
                          <Sparkles className="w-3 h-3" /> Advanced fieldworker
                        </span>
                      ) : null
                    )}
                    {u.role === "field" && !isSuspended && (
                      canSuspend ? (
                        <button
                          onClick={() => toggleTruckDriver(u)}
                          data-testid={`truck-driver-toggle-${u.id}`}
                          className={`mt-1.5 ml-1.5 inline-flex items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                            u.truck_driver
                              ? "border-[#FF5F15] bg-orange-50 text-[#FF5F15]"
                              : "border-zinc-200 text-zinc-400 hover:border-zinc-400"
                          }`}
                          title={u.truck_driver
                            ? "Truck driver — sees the Running Sheets page for carting & tipping. Click to switch off."
                            : "Make Truck driver — unlocks daily truck running sheets (carting aggregates & tipping)."}>
                          <Truck className="w-3 h-3" />
                          {u.truck_driver ? "Truck driver" : "Not a driver"}
                        </button>
                      ) : u.truck_driver ? (
                        <span className="mt-1.5 ml-1.5 inline-flex items-center gap-1 border border-[#FF5F15] bg-orange-50 text-[#FF5F15] px-2 py-1 text-[10px] font-bold uppercase tracking-wide"
                              data-testid={`truck-driver-badge-${u.id}`}>
                          <Truck className="w-3 h-3" /> Truck driver
                        </span>
                      ) : null
                    )}
                    {u.role === "field" && !isSuspended && canSuspend && (
                      <button onClick={() => toggleLandfillOperator(u)} aria-pressed={!!u.landfill_operator}
                        className="mt-1.5 ml-1.5 border px-2 py-1 text-xs">
                        Landfill Operator: {u.landfill_operator ? 'On' : 'Off'}
                      </button>
                    )}
                    {u.role === "field" && !isSuspended && (
                      canSuspend ? (
                        <button
                          onClick={() => toggleContractor(u)}
                          data-testid={`contractor-toggle-${u.id}`}
                          className={`mt-1.5 ml-1.5 inline-flex items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                            u.contractor
                              ? "border-[#FF5F15] bg-orange-50 text-[#FF5F15]"
                              : "border-zinc-200 text-zinc-400 hover:border-zinc-400"
                          }`}
                          title={u.contractor
                            ? `Contractor${u.contractor_rate ? ` @ $${u.contractor_rate}/hr` : ""} — click to edit rate/GST or remove.`
                            : "Mark as Contractor — they invoice you from approved timesheets instead of payroll."}>
                          <FileText className="w-3 h-3" />
                          {u.contractor ? `Contractor${u.contractor_rate ? ` $${u.contractor_rate}/hr` : ""}` : "Not a contractor"}
                        </button>
                      ) : u.contractor ? (
                        <span className="mt-1.5 ml-1.5 inline-flex items-center gap-1 border border-[#FF5F15] bg-orange-50 text-[#FF5F15] px-2 py-1 text-[10px] font-bold uppercase tracking-wide"
                              data-testid={`contractor-badge-${u.id}`}>
                          <FileText className="w-3 h-3" /> Contractor
                        </span>
                      ) : null
                    )}
                  </div>
                </div>

                {/* HR at-a-glance — click any chip to open the full HR panel */}
                {canSuspend && (
                  <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`hr-chips-${u.id}`}>
                    <button onClick={() => setHrMember(u)}
                      className={`inline-flex items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                        !lic.number && !lic.expiry ? "border-zinc-200 text-zinc-400 hover:border-zinc-400"
                        : licDays != null && licDays < 0 ? "border-red-400 bg-red-50 text-red-700"
                        : licDays != null && licDays < 60 ? "border-amber-400 bg-amber-50 text-amber-700"
                        : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}
                      data-testid={`chip-licence-${u.id}`}>
                      <IdCard className="w-3 h-3" />
                      {!lic.number && !lic.expiry ? "No licence"
                        : licDays != null && licDays < 0 ? "Licence expired"
                        : licDays != null && licDays < 60 ? `Licence ${licDays}d left`
                        : `Licence${lic.licence_class ? ` · Cl ${lic.licence_class}` : ""}`}
                    </button>
                    <button onClick={() => setHrMember(u)}
                      className={`inline-flex items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${hasSizes ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-400 hover:border-zinc-400"}`}
                      data-testid={`chip-sizes-${u.id}`}>
                      <Shirt className="w-3 h-3" /> {hasSizes ? "Sizes on file" : "No sizes"}
                    </button>
                    <button onClick={() => setHrMember(u)}
                      className={`inline-flex items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${u.contract_path ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-400 hover:border-zinc-400"}`}
                      data-testid={`chip-contract-${u.id}`}>
                      <FileText className="w-3 h-3" /> {u.contract_path ? "Contract" : "No contract"}
                    </button>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <BPill label="Annual" value={b.annual} />
                  <BPill label="Sick" value={b.sick} />
                  <BPill label="Bereavement" value={b.bereavement} />
                </div>
                {u.hourly_rate > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-200 flex items-center justify-between text-xs">
                    <span className="label-tag">Pay rate</span>
                    <span className="metric font-bold">${Number(u.hourly_rate).toFixed(2)}/hr</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        {editing && <BalanceEditor target={editing} onDone={() => { setEditing(null); load(); }} />}
      </Dialog>

      <Dialog open={!!myRateTarget} onOpenChange={(v) => { if (!v) setMyRateTarget(null); }}>
        {myRateTarget && <MyRateEditor target={myRateTarget} onDone={() => { setMyRateTarget(null); load(); }} />}
      </Dialog>

      <Dialog open={!!hrMember} onOpenChange={(v) => { if (!v) setHrMember(null); }}>
        {hrMember && (
          <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-y-auto" data-testid="team-hr-dialog">
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-tight text-xl">{hrMember.name}</DialogTitle>
              <DialogDescription>Licence, uniform sizes & employment contract</DialogDescription>
            </DialogHeader>
            <TeamMemberHr member={hrMember} onChanged={async () => {
              const { data } = await api.get("/users");
              setItems(data);
              const fresh = data.find((x) => x.id === hrMember.id);
              if (fresh) setHrMember(fresh);
            }} />
          </DialogContent>
        )}
      </Dialog>

      <Dialog open={!!contractorDialog} onOpenChange={(v) => { if (!v) setContractorDialog(null); }}>
        {contractorDialog && (
          <ContractorSettingsDialog
            target={contractorDialog}
            onDone={() => { setContractorDialog(null); load(); }}
          />
        )}
      </Dialog>
    </PageContainer>
  );
}

function WorkforceDirectory({
  capacity, seatLimit, setSeatLimit,
  user, isAdmin, canInvite, canSuspend, canEditOwnRate, items, filtered, pendingInvites, workforceMetrics,
  competencyFor, todayFor, q, setQ, roleFilter, setRoleFilter, statusFilter, setStatusFilter, competencyFilter, setCompetencyFilter,
  view, setViewPersist, invitesOpen, setInvitesOpen, copiedId, copyInviteLink, revokeInvite, load,
  inviteOpen, setInviteOpen, addOpen, setAddOpen, editing, setEditing, myRateTarget, setMyRateTarget,
  hrMember, setHrMember, contractorDialog, setContractorDialog, changeRole, suspend, unsuspend, remove,
  toggleAdvancedField, toggleTruckDriver, toggleLandfillOperator, toggleContractor, openWorker,
}) {
  return (
    <PageContainer variant="wide" data-testid="team-page">
      <PageHeader
        eyebrow="Workforce // People"
        title="Team"
        subtitle="Manage your people, roles, access, competencies and workforce availability."
        action={canInvite && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="add-person-button">
                <Plus className="mr-1.5 h-4 w-4" /> Add person <ChevronDown className="ml-1 h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onSelect={() => setInviteOpen(true)} data-testid="invite-member-button"><Mail className="mr-2 h-4 w-4" /> Send invite</DropdownMenuItem>
              {isAdmin && <DropdownMenuItem onSelect={() => setAddOpen(true)} data-testid="add-member-button"><Plus className="mr-2 h-4 w-4" /> Add directly</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      />

      <SeatLimitDialog detail={seatLimit} onClose={() => setSeatLimit(null)} canManage={isAdmin} />
      {capacity && <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-300" data-testid="team-plan-capacity">
        <strong>{capacity.active_users}{capacity.user_limit != null ? ` of ${capacity.user_limit}` : ""} active users</strong>
        {" · "}{capacity.plan_display_name} plan · {capacity.available_capacity == null ? "Custom / unlimited capacity" : `${capacity.available_capacity} spaces available`}
      </p>}

      <div className="mb-4 grid min-h-[72px] grid-cols-2 gap-px overflow-hidden rounded-sm border border-zinc-200 bg-zinc-200 dark:border-white/10 dark:bg-white/10 sm:grid-cols-3 min-[1200px]:grid-cols-6" data-testid="workforce-summary">
        {[["TOTAL PEOPLE", workforceMetrics.total], ["FIELD", workforceMetrics.field], ["OFFICE", workforceMetrics.office], ["ON LEAVE", workforceMetrics.onLeave], ["COMPETENCY ISSUES", workforceMetrics.issues], ["PENDING INVITES", workforceMetrics.pendingInvites]].map(([label, value]) => (
          <div key={label} className="bg-white px-3 py-3 dark:bg-[#0F141C]"><div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</div><div className="mt-1 text-2xl font-black tracking-tight text-zinc-900 dark:text-white">{value}</div></div>
        ))}
      </div>

      {pendingInvites.length > 0 && <div className="mb-4 rounded-sm border border-zinc-200 bg-white dark:border-white/10 dark:bg-[#0F141C]" data-testid="pending-invites-section">
        <button onClick={() => setInvitesOpen((open) => !open)} className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left" data-testid="pending-invites-toggle">
          <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 dark:text-zinc-300"><Send className="h-3 w-3 text-[#FF5F15]" /> Pending invites <strong className="text-[#FF5F15]">{pendingInvites.length}</strong></span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{invitesOpen ? "Hide" : "View"}<ChevronDown className={`ml-1 inline h-3.5 w-3.5 ${invitesOpen ? "rotate-180" : ""}`} /></span>
        </button>
        {invitesOpen && <div className="border-t border-zinc-200 px-3 dark:border-white/10">
          <div className="hidden grid-cols-[2fr_1fr_1fr_auto] gap-3 py-2 text-[9px] font-bold uppercase tracking-wider text-zinc-500 md:grid"><span>Person / Email</span><span>Role</span><span>Expires</span><span>Actions</span></div>
          {pendingInvites.map((inv) => <div key={inv.id} className="grid gap-2 border-t border-zinc-200 py-3 first:border-t-0 md:grid-cols-[2fr_1fr_1fr_auto] md:items-center dark:border-white/10" data-testid={`invite-row-${inv.id}`}>
            <div className="min-w-0"><div className="truncate text-sm font-bold">{inv.name}</div><div className="truncate font-mono text-xs text-zinc-500">{inv.email}</div></div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{ROLE_LABEL[inv.role] || inv.role}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{new Date(inv.expires_at).toLocaleDateString("en-NZ", { month: "short", day: "numeric" })}</div>
            <div className="flex items-center gap-2"><Button size="sm" variant="outline" className={`h-8 rounded-sm text-[10px] font-bold uppercase ${copiedId === inv.id ? "border-green-600 text-green-700" : "border-[#FF5F15] text-[#FF5F15]"}`} onClick={() => copyInviteLink(inv)} disabled={!inv.accept_url} data-testid={`invite-copy-${inv.id}`}>{copiedId === inv.id ? <><Check className="mr-1 h-3.5 w-3.5" /> Copied</> : <><Copy className="mr-1 h-3.5 w-3.5" /> Copy link</>}</Button><Button size="sm" variant="ghost" className="h-8 rounded-sm text-[10px] font-bold uppercase text-zinc-500 hover:text-red-600" onClick={() => revokeInvite(inv.id)} data-testid={`invite-revoke-${inv.id}`}>Revoke</Button></div>
          </div>)}
        </div>}
      </div>}

      <div className="mb-4 flex flex-col gap-3 rounded-sm border border-zinc-200 bg-white p-3 dark:border-white/10 dark:bg-[#0F141C] xl:flex-row xl:items-center" data-testid="team-toolbar">
        <div className="relative min-w-0 flex-1 xl:max-w-sm"><Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people..." className="rounded-sm pl-9" data-testid="team-search-input" /></div>
        <div className="flex flex-wrap items-center gap-2 xl:ml-auto">
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-[10px] font-bold uppercase tracking-wider dark:border-white/10 dark:bg-[#101620]" data-testid="team-role-filter">{ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role === "all" ? "ROLE" : ROLE_LABEL[role] || role}</option>)}</select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-[10px] font-bold uppercase tracking-wider dark:border-white/10 dark:bg-[#101620]" data-testid="team-status-filter"><option value="all">STATUS</option><option value="active">ACTIVE</option><option value="inactive">INACTIVE</option></select>
          <select value={competencyFilter} onChange={(e) => setCompetencyFilter(e.target.value)} className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-[10px] font-bold uppercase tracking-wider dark:border-white/10 dark:bg-[#101620]" data-testid="team-competency-filter"><option value="all">COMPETENCY</option><option value="attention">NEEDS ATTENTION</option><option value="current">CURRENT</option><option value="none">NO RECORDS</option></select>
          <div className="flex items-center rounded-sm border border-zinc-300 bg-white p-0.5 dark:border-white/15 dark:bg-[#101620]" data-testid="team-view-toggle"><button onClick={() => setViewPersist("list")} className={`inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-[10px] font-bold uppercase ${view === "list" ? "bg-[#FF5F15] text-white" : "text-zinc-500"}`} data-testid="team-view-list"><LayoutList className="h-3.5 w-3.5" /> List</button><button onClick={() => setViewPersist("cards")} className={`inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-[10px] font-bold uppercase ${view === "cards" ? "bg-[#FF5F15] text-white" : "text-zinc-500"}`} data-testid="team-view-cards"><LayoutGrid className="h-3.5 w-3.5" /> Cards</button></div>
        </div>
      </div>

      {items.length === 0 ? <EmptyState title="No team members yet" description="Add your first crew member to get started." /> : !filtered.length ? <EmptyState title="No matches" description="Try a different search or filter." /> : view === "cards" ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="team-cards">{filtered.map((member) => <WorkforceCard key={member.id} member={member} user={user} canSuspend={canSuspend} canEditOwnRate={canEditOwnRate} competency={competencyFor(member)} today={todayFor(member)} onOpen={() => openWorker(member)} onRate={() => setMyRateTarget(member)} onEdit={() => setEditing(member)} onSuspend={() => member.suspended_at ? unsuspend(member) : suspend(member)} onRemove={() => remove(member.id)} onRole={changeRole} isAdmin={isAdmin} onAdvanced={() => toggleAdvancedField(member)} onDriver={() => toggleTruckDriver(member)} onLandfill={() => toggleLandfillOperator(member)} onContractor={() => toggleContractor(member)} />)}</div>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-zinc-200 bg-white dark:border-white/10 dark:bg-[#0F141C]" data-testid="team-list"><div className="min-w-[1020px]"><div className="grid grid-cols-[minmax(230px,2fr)_135px_105px_170px_170px_100px_48px] gap-3 border-b border-zinc-200 bg-zinc-50 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:border-white/10 dark:bg-white/[0.03]"><span>Person</span><span>Role</span><span>Status</span><span>Competencies</span><span>Today</span><span>Rate</span><span /></div>{filtered.map((member) => <WorkforceRow key={member.id} member={member} user={user} canSuspend={canSuspend} canEditOwnRate={canEditOwnRate} competency={competencyFor(member)} today={todayFor(member)} onOpen={() => openWorker(member)} onRate={() => setMyRateTarget(member)} onEdit={() => setEditing(member)} onSuspend={() => member.suspended_at ? unsuspend(member) : suspend(member)} onRemove={() => remove(member.id)} onRole={changeRole} isAdmin={isAdmin} onAdvanced={() => toggleAdvancedField(member)} onDriver={() => toggleTruckDriver(member)} onLandfill={() => toggleLandfillOperator(member)} onContractor={() => toggleContractor(member)} />)}</div></div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}><InviteMemberForm onDone={() => { setInviteOpen(false); load(); }} /></Dialog>
      <Dialog open={addOpen} onOpenChange={setAddOpen}><AddMemberForm onLimit={detail => { setAddOpen(false); setSeatLimit(detail); load(); }} onDone={() => { setAddOpen(false); load(); }} /></Dialog>
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>{editing && <BalanceEditor target={editing} onDone={() => { setEditing(null); load(); }} />}</Dialog>
      <Dialog open={!!myRateTarget} onOpenChange={(open) => { if (!open) setMyRateTarget(null); }}>{myRateTarget && <MyRateEditor target={myRateTarget} onDone={() => { setMyRateTarget(null); load(); }} />}</Dialog>
      <Dialog open={!!hrMember} onOpenChange={(open) => { if (!open) setHrMember(null); }}>{hrMember && <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto rounded-sm" data-testid="team-hr-dialog"><DialogHeader><DialogTitle className="font-display text-xl uppercase tracking-tight">{hrMember.name}</DialogTitle><DialogDescription>Licence, uniform sizes & employment contract</DialogDescription></DialogHeader><TeamMemberHr member={hrMember} onChanged={async () => { const { data } = await api.get("/users"); setHrMember(data.find((x) => x.id === hrMember.id) || hrMember); load(); }} /></DialogContent>}</Dialog>
      <Dialog open={!!contractorDialog} onOpenChange={(open) => { if (!open) setContractorDialog(null); }}>{contractorDialog && <ContractorSettingsDialog target={contractorDialog} onDone={() => { setContractorDialog(null); load(); }} />}</Dialog>
    </PageContainer>
  );
}

// Leave all native controls and portalled menus to their existing handlers.
export function employeeNavigation(onOpen, name) {
  return {
    tabIndex: 0, role: 'link', 'aria-label': `Open ${name}'s profile`,
    onClick: event => {
      if (!event.currentTarget.contains(event.target) || event.target.closest('button,a,input,select,textarea,[role="button"],[role="combobox"],[role="menuitem"]')) return;
      onOpen();
    },
    onKeyDown: event => {
      if (event.target === event.currentTarget && event.key === 'Enter') { event.preventDefault(); onOpen(); }
    },
  };
}

function WorkforcePerson({ member, user, competency, onOpen }) {
  const isSuspended = !!member.suspended_at;
  const initials = (member.name || "?").split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  return <div className="flex min-w-0 items-center gap-3" onClick={(event) => { event.stopPropagation(); onOpen(); }}><div className={`h-10 w-10 shrink-0 overflow-hidden rounded-sm border border-zinc-300 dark:border-white/10 ${isSuspended ? "opacity-50" : ""}`}>{member.avatar_path ? <AuthImage path={member.avatar_path} alt={member.name} className="h-full w-full object-cover" /> : <Avatar className="h-full w-full rounded-sm"><AvatarFallback className="rounded-sm bg-zinc-700 font-display text-sm font-black text-white">{initials}</AvatarFallback></Avatar>}</div><div className="min-w-0"><div className="truncate text-sm font-bold uppercase tracking-tight text-zinc-900 dark:text-white">{member.name}{member.id === user.id && <span className="ml-2 text-[9px] font-normal normal-case tracking-normal text-zinc-400">YOU</span>}</div><div className="truncate text-[11px] text-zinc-500">{member.email}</div></div></div>;
}

function CompetencyCell({ competency }) {
  if (!competency.total) return <span className="text-[10px] font-bold uppercase text-zinc-400">No records</span>;
  return <div className="text-[10px] font-bold uppercase tracking-wide">{competency.current > 0 && <span className="text-emerald-700">✓ {competency.current} current</span>}{competency.expiring > 0 && <span className="ml-2 text-amber-700">⚠ {competency.expiring} expiring</span>}{competency.expired > 0 && <span className="ml-2 text-red-700">! {competency.expired} expired</span>}</div>;
}

export function WorkforceRow({ member, user, competency, today, canSuspend, canEditOwnRate, isAdmin, onOpen, onRate, onEdit, onSuspend, onRemove, onRole, onAdvanced, onDriver, onLandfill, onContractor }) {
  const isSelf = member.id === user.id;
  const canManage = (isAdmin || canSuspend) && !isSelf && member.role !== "super_admin";
  return <div className={`cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15] grid min-h-[60px] grid-cols-[minmax(230px,2fr)_135px_105px_170px_170px_100px_48px] items-center gap-3 border-b border-zinc-100 px-4 py-2.5 transition-colors last:border-b-0 hover:bg-zinc-50 dark:border-white/5 dark:hover:bg-white/[0.03] ${member.suspended_at ? "bg-zinc-50/60 dark:bg-white/[0.02]" : ""}`} data-testid={`team-row-${member.id}`} {...employeeNavigation(onOpen, member.name)}>
    <WorkforcePerson member={member} user={user} competency={competency} onOpen={onOpen} />
    <div onClick={(event) => event.stopPropagation()}>{isAdmin && !isSelf && member.role !== "super_admin" && !member.suspended_at ? <Select value={member.role} onValueChange={(value) => onRole(member.id, value, member.role)}><SelectTrigger className="h-7 w-[135px] rounded-sm border-zinc-300 px-2 text-[10px] font-bold uppercase tracking-wider" data-testid={`role-select-${member.id}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="office">Office</SelectItem><SelectItem value="field">Field worker</SelectItem><SelectItem value="mechanic">Mechanic</SelectItem><SelectItem value="quantity_surveyor">Quantity Surveyor</SelectItem></SelectContent></Select> : <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300">{ROLE_LABEL[member.role] || member.role}</span>}</div>
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${member.suspended_at ? "text-red-700" : today.type === "leave" ? "text-amber-700" : "text-emerald-700"}`}><span className={`h-1.5 w-1.5 rounded-full ${member.suspended_at ? "bg-red-600" : today.type === "leave" ? "bg-amber-500" : "bg-emerald-500"}`} />{member.suspended_at ? "Inactive" : today.type === "leave" ? "On leave" : "Active"}</span>
    <CompetencyCell competency={competency} />
    <span className={`truncate text-[11px] ${today.type === "leave" ? "font-bold text-amber-700" : "text-zinc-600 dark:text-zinc-300"}`}>{today.label}</span>
    <span className="text-xs font-bold text-zinc-700 dark:text-zinc-300">{canSuspend && member.hourly_rate > 0 ? `$${Number(member.hourly_rate).toFixed(0)}/hr` : "—"}</span>
    <div onClick={(event) => event.stopPropagation()}><DropdownMenu><DropdownMenuTrigger asChild><button className="flex h-8 w-8 items-center justify-center rounded-sm text-zinc-500 hover:bg-zinc-100 dark:hover:bg-white/10" aria-label={`Actions for ${member.name}`} data-testid={`team-actions-${member.id}`}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onSelect={onOpen}>View profile</DropdownMenuItem>{isSelf && canEditOwnRate && <DropdownMenuItem onSelect={onRate}>Set my rate</DropdownMenuItem>}{canManage && <><DropdownMenuItem onSelect={onEdit}>Edit rates & leave</DropdownMenuItem>{member.role === "field" && <><DropdownMenuItem onSelect={onAdvanced}>Toggle advanced field</DropdownMenuItem><DropdownMenuItem onSelect={onDriver}>Toggle truck driver</DropdownMenuItem><DropdownMenuItem onSelect={onLandfill}>Toggle Landfill Operator</DropdownMenuItem><DropdownMenuItem onSelect={onContractor}>Contractor settings</DropdownMenuItem></>}<DropdownMenuSeparator /><DropdownMenuItem onSelect={onSuspend}>{member.suspended_at ? "Reactivate" : "Suspend access"}</DropdownMenuItem>{isAdmin && <DropdownMenuItem className="text-red-600" onSelect={onRemove}>Remove from company</DropdownMenuItem>}</>}</DropdownMenuContent></DropdownMenu></div>
  </div>;
}

export function WorkforceCard({ member, user, competency, today, canSuspend, canEditOwnRate, isAdmin, onOpen, onRate, onEdit, onSuspend, onRemove, onRole, onAdvanced, onDriver, onLandfill, onContractor }) {
  const canManage = (isAdmin || canSuspend) && member.id !== user.id && member.role !== "super_admin";
  return <div className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15] rounded-sm border border-zinc-200 bg-white p-4 dark:border-white/10 dark:bg-[#0F141C]" data-testid={`team-card-${member.id}`} {...employeeNavigation(onOpen, member.name)}><div className="flex items-start justify-between gap-3"><WorkforcePerson member={member} user={user} competency={competency} onOpen={onOpen} /><DropdownMenu><DropdownMenuTrigger asChild><button className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-zinc-500 hover:bg-zinc-100 dark:hover:bg-white/10" aria-label={`Actions for ${member.name}`}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onSelect={onOpen}>View profile</DropdownMenuItem>{member.id === user.id && canEditOwnRate && <DropdownMenuItem onSelect={onRate}>Set my rate</DropdownMenuItem>}{canManage && <><DropdownMenuItem onSelect={onEdit}>Edit rates & leave</DropdownMenuItem>{member.role === "field" && <><DropdownMenuItem onSelect={onAdvanced}>Toggle advanced field</DropdownMenuItem><DropdownMenuItem onSelect={onDriver}>Toggle truck driver</DropdownMenuItem><DropdownMenuItem onSelect={onLandfill}>Toggle Landfill Operator</DropdownMenuItem><DropdownMenuItem onSelect={onContractor}>Contractor settings</DropdownMenuItem></>}<DropdownMenuSeparator /><DropdownMenuItem onSelect={onSuspend}>{member.suspended_at ? "Reactivate" : "Suspend access"}</DropdownMenuItem>{isAdmin && <DropdownMenuItem className="text-red-600" onSelect={onRemove}>Remove from company</DropdownMenuItem>}</>}</DropdownMenuContent></DropdownMenu></div><div className="mt-4 grid grid-cols-2 gap-3 border-t border-zinc-100 pt-3 text-xs dark:border-white/5"><div><div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Role</div><div className="mt-1 font-bold uppercase">{ROLE_LABEL[member.role] || member.role}</div></div><div><div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Status</div><div className={`mt-1 inline-flex items-center gap-1.5 font-bold uppercase ${member.suspended_at ? "text-red-700" : today.type === "leave" ? "text-amber-700" : "text-emerald-700"}`}><span className={`h-1.5 w-1.5 rounded-full ${member.suspended_at ? "bg-red-600" : today.type === "leave" ? "bg-amber-500" : "bg-emerald-500"}`} />{member.suspended_at ? "Inactive" : today.type === "leave" ? "On leave" : "Active"}</div></div><div><div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Today</div><div className="mt-1 truncate font-semibold">{today.label}</div></div><div><div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Competencies</div><div className="mt-1"><CompetencyCell competency={competency} /></div></div><div><div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Rate</div><div className="mt-1 font-bold">{canSuspend && member.hourly_rate > 0 ? `$${Number(member.hourly_rate).toFixed(0)}/hr` : "—"}</div></div></div></div>;
}

function ContractorSettingsDialog({ target, onDone }) {
  const [rate, setRate] = useState(target.contractor_rate ?? "");
  const [gstReg, setGstReg] = useState(!!target.gst_registered);
  const [gstNum, setGstNum] = useState(target.gst_number || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${target.id}/contractor`, {
        contractor: true,
        contractor_rate: rate === "" ? null : Number(rate),
        gst_registered: gstReg,
        gst_number: gstNum,
      });
      toast.success("Contractor settings saved");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const removeContractor = async () => {
    if (!window.confirm(`${target.name} will no longer be a contractor. Continue?`)) return;
    try {
      await api.patch(`/users/${target.id}/contractor`, { contractor: false });
      toast.success("Contractor removed");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <DialogContent className="rounded-sm max-w-md" data-testid="contractor-settings-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-xl">Contractor — {target.name}</DialogTitle>
        <DialogDescription>
          Agreed hourly rate & GST. They invoice you from their approved timesheets instead of going through payroll.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="label-tag">Hourly rate ($)</Label>
          <Input type="number" step="0.5" min="0" value={rate} onChange={(e) => setRate(e.target.value)}
                 placeholder="e.g. 65" className="mt-1.5 rounded-sm border-zinc-400" data-testid="contractor-dialog-rate" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={gstReg} onChange={(e) => setGstReg(e.target.checked)}
                 className="accent-[#FF5F15]" data-testid="contractor-dialog-gst" />
          <span className="text-xs font-bold uppercase tracking-wider">GST registered (adds 15% to invoices)</span>
        </label>
        {gstReg && (
          <div>
            <Label className="label-tag">GST number</Label>
            <Input value={gstNum} onChange={(e) => setGstNum(e.target.value)} placeholder="123-456-789"
                   className="mt-1.5 rounded-sm border-zinc-400" data-testid="contractor-dialog-gst-number" />
          </div>
        )}
      </div>
      <DialogFooter className="flex-row justify-between sm:justify-between">
        <Button variant="ghost" onClick={removeContractor}
                className="rounded-sm text-red-600 hover:text-red-700 uppercase tracking-wider font-bold text-xs"
                data-testid="contractor-dialog-remove">
          Remove contractor
        </Button>
        <Button onClick={save} disabled={busy}
                className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
                data-testid="contractor-dialog-save">
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function BPill({ label, value }) {
  return (
    <div className="p-2 bg-zinc-50 border border-zinc-200">
      <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="metric text-sm font-bold">{(value ?? 0).toFixed?.(1) ?? "—"}</div>
    </div>
  );
}

function InviteMemberForm({ onDone }) {
  const [form, setForm] = useState({ name: "", email: "", role: "field" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/invites", form);
      toast.success(`Invite emailed to ${form.email}`);
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">Send invite</DialogTitle>
        <DialogDescription>We&rsquo;ll email a link to set their own password. The invite expires in 7 days.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="label-tag">Full name</Label>
          <Input required value={form.name} onChange={set("name")} className="mt-2 rounded-sm border-zinc-400" placeholder="Mere Walker" data-testid="invite-name-input" />
        </div>
        <div>
          <Label className="label-tag">Work email</Label>
          <Input required type="email" value={form.email} onChange={set("email")} className="mt-2 rounded-sm border-zinc-400" placeholder="mere@yourcompany.co.nz" data-testid="invite-email-input" />
        </div>
        <div>
          <Label className="label-tag">Role</Label>
          <Select value={form.role} onValueChange={set("role")}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="field">Field worker</SelectItem>
              <SelectItem value="mechanic">Mechanic</SelectItem>
              <SelectItem value="quantity_surveyor">Quantity Surveyor</SelectItem>
              <SelectItem value="office">Office staff</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {err && <div className="border border-red-300 bg-red-50 text-red-700 p-3 text-sm">{err}</div>}
        <DialogFooter>
          <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="invite-submit-button">
            {busy ? "Sending…" : <><Send className="w-4 h-4 mr-2" /> Send invite</>}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function AddMemberForm({ onDone, onLimit }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "field" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/users", form);
      toast.success(`Added ${form.name} to your team`);
      onDone();
    } catch (e) {
      if (seatLimitDetail(e) && onLimit) onLimit(seatLimitDetail(e));
      else setErr(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">Add team member</DialogTitle>
        <DialogDescription>They&rsquo;ll be added to your company immediately. Share their email and the initial password — they can sign in straight away.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="label-tag">Full name</Label>
          <Input required value={form.name} onChange={set("name")} className="mt-2 rounded-sm border-zinc-400" placeholder="Mere Walker" data-testid="member-name-input" />
        </div>
        <div>
          <Label className="label-tag">Work email</Label>
          <Input required type="email" value={form.email} onChange={set("email")} className="mt-2 rounded-sm border-zinc-400" placeholder="mere@yourcompany.co.nz" data-testid="member-email-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Initial password</Label>
            <Input required type="text" minLength={6} value={form.password} onChange={set("password")} className="mt-2 rounded-sm border-zinc-400" placeholder="6+ characters" data-testid="member-password-input" />
          </div>
          <div>
            <Label className="label-tag">Role</Label>
            <Select value={form.role} onValueChange={set("role")}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="member-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="office">Office / PM</SelectItem>
                <SelectItem value="field">Field worker</SelectItem>
                <SelectItem value="mechanic">Mechanic</SelectItem>
                <SelectItem value="quantity_surveyor">Quantity Surveyor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {err && <div className="border border-red-300 bg-red-50 text-red-700 p-3 text-sm" data-testid="member-error">{err}</div>}
        <DialogFooter>
          <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="member-submit-button">
            {busy ? "Adding..." : "Add to team"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function MyRateEditor({ target, onDone }) {
  const [form, setForm] = useState({
    hourly_rate: target.hourly_rate ?? 0,
    charge_out_rate: target.charge_out_rate ?? "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/users/${target.id}/hourly-rate`, {
        hourly_rate: Number(form.hourly_rate) || 0,
        charge_out_rate: form.charge_out_rate === "" || form.charge_out_rate == null ? 0 : Number(form.charge_out_rate),
      });
      toast.success("Your rate has been updated");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="rounded-sm" data-testid="my-rate-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">Set my rate</DialogTitle>
        <DialogDescription>Your own charge-out rate (billed to clients when invoicing your hours) and pay rate. Only you can change these.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Charge-out rate (NZD)</Label>
            <div className="mt-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-mono">$</span>
              <Input type="number" step="0.01" min="0" value={form.charge_out_rate} onChange={set("charge_out_rate")} placeholder="e.g. 120" className="rounded-sm border-zinc-400 pl-6" data-testid="my-charge-out-rate-input" />
            </div>
            <div className="mt-1 text-[10px] text-zinc-400">Auto-fills the $/hr when your hours are invoiced</div>
          </div>
          <div>
            <Label className="label-tag">Hourly pay rate (NZD)</Label>
            <div className="mt-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-mono">$</span>
              <Input type="number" step="0.01" min="0" value={form.hourly_rate} onChange={set("hourly_rate")} className="rounded-sm border-zinc-400 pl-6" data-testid="my-hourly-rate-input" />
            </div>
            <div className="mt-1 text-[10px] text-zinc-400">Used for payroll & job costing</div>
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="my-rate-submit-button">
            {busy ? "Saving..." : "Save my rate"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function BalanceEditor({ target, onDone }) {
  const b = target.leave_balances || {};
  const [form, setForm] = useState({
    annual: b.annual ?? 20,
    sick: b.sick ?? 10,
    bereavement: b.bereavement ?? 3,
    hourly_rate: target.hourly_rate ?? 0,
    charge_out_rate: target.charge_out_rate ?? "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await Promise.all([
        api.patch(`/users/${target.id}/leave-balances`, {
          annual: Number(form.annual),
          sick: Number(form.sick),
          bereavement: Number(form.bereavement),
        }),
        api.patch(`/users/${target.id}/hourly-rate`, {
          hourly_rate: Number(form.hourly_rate) || 0,
          charge_out_rate: form.charge_out_rate === "" || form.charge_out_rate == null ? 0 : Number(form.charge_out_rate),
        }),
      ]);
      toast.success(`Updated ${target.name}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">{target.name}</DialogTitle>
        <DialogDescription>Set the hourly pay rate (for payroll) and leave balances (NZ statutory minimums shown).</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Hourly pay rate (NZD)</Label>
            <div className="mt-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-mono">$</span>
              <Input type="number" step="0.01" min="0" value={form.hourly_rate} onChange={set("hourly_rate")} className="rounded-sm border-zinc-400 pl-6" data-testid="hourly-rate-input" />
            </div>
            <div className="mt-1 text-[10px] text-zinc-400">What they cost you — payroll & job costing</div>
          </div>
          <div>
            <Label className="label-tag">Charge-out rate (NZD)</Label>
            <div className="mt-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-mono">$</span>
              <Input type="number" step="0.01" min="0" value={form.charge_out_rate} onChange={set("charge_out_rate")} placeholder="e.g. 95" className="rounded-sm border-zinc-400 pl-6" data-testid="charge-out-rate-input" />
            </div>
            <div className="mt-1 text-[10px] text-zinc-400">Auto-fills the $/hr when invoicing their hours</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-zinc-200">
          <div>
            <Label className="label-tag">Annual</Label>
            <Input type="number" step="0.5" value={form.annual} onChange={set("annual")} className="mt-2 rounded-sm border-zinc-400" data-testid="balance-annual-input" />
          </div>
          <div>
            <Label className="label-tag">Sick</Label>
            <Input type="number" step="0.5" value={form.sick} onChange={set("sick")} className="mt-2 rounded-sm border-zinc-400" data-testid="balance-sick-input" />
          </div>
          <div>
            <Label className="label-tag">Bereavement</Label>
            <Input type="number" step="0.5" value={form.bereavement} onChange={set("bereavement")} className="mt-2 rounded-sm border-zinc-400" data-testid="balance-bereavement-input" />
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="balance-submit-button">
            {busy ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
